/**
 * Same-origin API client. The session rides on the httpOnly cookie; no token is ever
 * readable here. Writes carry the session's CSRF token (the API requires it for
 * cookie-authenticated writes). A 401 goes to one app-wide handler (back to login).
 */
import { getCsrfToken } from './session'

export class ApiError extends Error {
    readonly status: number

    constructor(status: number, message: string) {
        super(message)
        this.name = 'ApiError'
        this.status = status
    }
}

let onUnauthorized: (() => void) | null = null

/** Registered once by the app; called on any 401 except where a request opts out. */
export function setUnauthorizedHandler(handler: (() => void) | null): void {
    onUnauthorized = handler
}

interface RequestOptions {
    signal?: AbortSignal
    /** Default true. Session probes (/auth/me) and login opt out: a 401 there is an answer, not an error. */
    handleUnauthorized?: boolean
}

async function request<T>(method: string, path: string, body: unknown, options: RequestOptions = {}): Promise<T> {
    const headers: Record<string, string> = { Accept: 'application/json' }
    if (body !== undefined) headers['Content-Type'] = 'application/json'
    if (method !== 'GET') {
        const csrf = getCsrfToken()
        if (csrf) headers['X-CSRF-Token'] = csrf
    }

    const response = await fetch(path, {
        method,
        credentials: 'same-origin',
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
        signal: options.signal,
    })

    if (response.status === 401 && options.handleUnauthorized !== false) {
        onUnauthorized?.()
    }
    if (!response.ok) {
        throw new ApiError(response.status, `${method} ${path} failed with HTTP ${response.status}`)
    }
    if (response.status === 204) return undefined as T
    return (await response.json()) as T
}

export const api = {
    get: <T>(path: string, options?: RequestOptions) => request<T>('GET', path, undefined, options),
    post: <T>(path: string, body?: unknown, options?: RequestOptions) => request<T>('POST', path, body ?? {}, options),
    patch: <T>(path: string, body?: unknown, options?: RequestOptions) => request<T>('PATCH', path, body ?? {}, options),
}

/** Retry only what can succeed on retry: network failures and 5xx, at most twice. */
export function shouldRetry(failureCount: number, error: unknown): boolean {
    if (failureCount >= 2) return false
    if (error instanceof ApiError) return error.status >= 500
    return !(error instanceof DOMException && error.name === 'AbortError')
}
